import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import CreateAccount from '../auth/createAccount';
import LoginAccount from '../auth/loginAccount';
import { CDN_BASE_URL, CDN_URL } from '../../constants';
import ForgotPassword from '../auth/forgotPassword';
import Logo from '../../assets/Logo.svg';
import { useLocation } from 'react-router-dom';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import { readLpSession } from '../../utils/session';
import AnimatedTapeAuth from './AnimatedTapeAuth';
import CountdownTimer from './CountdownTimer';

const AuthModal = ({ isOpen, onClose, selectedStyle }) => {
  // Returning visitor whose stored session has NO spendable token — the redirect
  // arm (the platform consumed it on arrival) or a resumed checkout that 401'd.
  // The account exists, so open on the login card with the email prefilled rather
  // than a signup form the backend can only reject. A session WITH a token stays
  // on signup: createAccount reopens the paywall popup with it (utils/session.js).
  // Password only — Google and magic-link users have no password to log in with;
  // their recovery lives on the signup card (Google button / email-only → OTP).
  const [authView, setAuthView] = useState(() => {
    // A Google OAuth return (?auth_token) is handled by the signup card's effect —
    // it's the only place that reopens the paywall popup — so it always wins over
    // the login card, otherwise the return would silently do nothing.
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('auth_token')
    ) {
      return 'signup';
    }
    const stored = readLpSession();
    return stored && !stored.authToken && stored.source === 'password'
      ? 'login'
      : 'signup';
  });
  const location = useLocation();
  const isHentai = location.pathname.includes('/create-ai-hentai');
  const isHalloween = location.pathname.includes('/halloween');

  const { content: value, magicLink } = useGrowthBookValues();
  const isMagicLink = magicLink === 'ON';

  const GB_VIDEOS = {
    Realistic: `${CDN_URL}/${value['realistic-auth-video']}`,
    Realistic2: `${CDN_URL}/${value['realistic-auth-video2']}`,
    Anime: `${CDN_URL}/${value['anime-auth-video']}`,
    Anime2: `${CDN_URL}/${value['anime-auth-video2']}`,
  };

  const GB_POSTERS = {
    Realistic: `${CDN_URL}/${value['realistic-auth-photo']}`,
    Realistic2: `${CDN_URL}/${value['realistic-auth-photo2']}`,
    Anime: `${CDN_URL}/${value['anime-auth-photo']}`,
    Anime2: `${CDN_URL}/${value['anime-auth-photo2']}`,
  };

  const getPath = (path) => {
    const prefix = isHalloween ? '/Halloween' : '';
    return `${CDN_BASE_URL}${prefix}${path}`;
  };

  // Memoize video/poster to prevent re-random on every render
  const authMedia = useMemo(() => {
    const videoMap = {
      Realistic: [
        {
          video:
            GB_VIDEOS.Realistic || getPath(`/Realistic/auth_realistic.mp4`),
          poster:
            GB_POSTERS.Realistic ||
            getPath(`/Realistic/first_frame_realistic.jpg`),
        },
        {
          video:
            GB_VIDEOS.Realistic2 || getPath(`/Realistic/auth_realistic_2.mp4`),
          poster:
            GB_POSTERS.Realistic2 ||
            getPath(`/Realistic/first_frame_realistic_2.jpg`),
        },
      ],
      Anime: [
        {
          video: GB_VIDEOS.Anime || getPath(`/Anime/auth_anime.mp4`),
          poster: GB_POSTERS.Anime || getPath(`/Anime/first_frame_anime.jpg`),
        },
        {
          video: GB_VIDEOS.Anime2 || getPath(`/Anime/auth_anime_2.mp4`),
          poster:
            GB_POSTERS.Anime2 || getPath(`/Anime/first_frame_anime_2.jpg`),
        },
      ],
    };

    const style = isHentai ? 'Anime' : selectedStyle || 'Realistic';
    const videos = videoMap[style] || videoMap.Realistic;
    return videos[Math.floor(Math.random() * videos.length)];
  }, [isHentai, selectedStyle, isHalloween]);

  if (!isOpen) return null;

  return (
    <div className={`auth-modal-overlay${isMagicLink ? ' magic-link' : ''}`}>
      {/* Video Background - visible on ALL screen sizes now */}
      <div className="video-bg">
        <video
          src={authMedia.video}
          poster={authMedia.poster}
          autoPlay
          muted
          loop
          playsInline
          className="auth-bg-video"
        />
      </div>
      <div className="logo">
        <img src={Logo} alt="" />
      </div>
      <div
        className={`auth-modal ${isHalloween ? 'auth-modal-halloween' : ''}`}
      >
        <div className="auth-modal-content">
          <AnimatedTapeAuth />
          <div className="account-countdown-banner">
            <CountdownTimer isAuth />
          </div>
          {/* Auth Form - now the only content, no image side */}
          <div className="auth-form-container">
            {isMagicLink ? (
              <CreateAccount onClose={onClose} />
            ) : (
              <>
                {authView === 'login' && (
                  <LoginAccount
                    onSwitchToSignup={() => setAuthView('signup')}
                    onSwitchToForgot={() => setAuthView('forgot')}
                  />
                )}
                {authView === 'signup' && (
                  <CreateAccount onContinue={() => setAuthView('login')} />
                )}
                {authView === 'forgot' && (
                  <ForgotPassword onBackToLogin={() => setAuthView('login')} />
                )}
              </>
            )}
          </div>

          {/* REMOVED: auth-image-container - video is now only in background */}
        </div>
      </div>
    </div>
  );
};

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedStyle: PropTypes.string,
};

export default AuthModal;
